import {
    applyAuthTokenInterceptor,
    setAuthTokens,
    clearAuthTokens,
    getAccessToken,
    getRefreshToken
} from 'axios-jwt'

import axios from 'axios'
import { apiConfig } from '../config'

const BASE_URL = apiConfig.endpoint
const axiosInstance = axios.create({ baseURL: BASE_URL })

let isInitialized = false

/**
 * Initializes the authentication token interceptor if it hasn't been initialized yet.
 *
 * @returns {void}
 */
const initialize = () => {
    if (isInitialized) return

    isInitialized = true
    applyAuthTokenInterceptor(axiosInstance, {
        requestRefresh,
        header: 'Authorization',
        headerPrefix: 'Bearer '
    })
}

const onAccessTokenChangedCallbacks = []

/**
 * Updates the access token and triggers callbacks for access token changes.
 *
 * @param {string | undefined} newAccessToken - The new access token to be set. If undefined, sets the access token to null.
 * @returns {void}
 */
const updateAccessToken = (newAccessToken) => {
    const accessToken =
        typeof newAccessToken === 'undefined' ? null : newAccessToken

    setAuthTokens({
        accessToken,
        refreshToken: ''
    })

    onAccessTokenChangedCallbacks.forEach((item) => {
        item.callback(accessToken)
    })
}

/**
 * Sends a file upload request to the specified endpoint with the given payload.
 *
 * @param {string} endopint - The API endpoint to which the file upload request is sent.
 * @param {Object} payload - The data payload to be uploaded, typically a FormData object containing the file(s).
 * @returns {Promise} A promise that resolves with the response from the server.
 */
const sentFileUploadRequest = (endopint, payload) => {
    const options = {
        method: 'POST',
        url: `${BASE_URL}${endopint}`,
        data: payload,
        responseType: 'json',
        responseEncoding: 'utf8',
        timeout: 3 * 60000,

        headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json'
        }
    }

    if (getAccessToken()) {
        options.headers['Authorization'] = `Bearer ${getAccessToken()}`
    }

    return axiosInstance(options).then((response) => {
        const { accessToken } = response.data.body
        // save tokens to storage
        if (
            typeof accessToken != 'undefined' &&
            getAccessToken() !== accessToken
        ) {
            updateAccessToken(accessToken)
        }

        return response
    })
}

const cancelTokens = {}

/**
 * Sends an HTTP POST request to a given endpoint with optional cancel token, timeout, and custom cancel token.
 *
 * @param {string} endpoint - The endpoint to which the request is sent.
 * @param {Object} payload - The payload data to be sent in the request body.
 * @param {boolean} [withCancelToken=false] - Indicates whether to use a cancel token for this request.
 * @param {number|null} [timeout=null] - The timeout duration for the request in milliseconds.
 * @param {Object|null} [customCancelToken=null] - A custom cancel token, if provided.
 * @returns {Promise<Object>} A promise that resolves to the response of the request.
 */
const sentRequest = (
    endopint,
    payload,
    withCancelToken = false,
    timeout = null,
    customCancelToken = null,
    method = 'POST',
    contentType = 'application/x-www-form-urlencoded'
) => {
    if (cancelTokens.hasOwnProperty(endopint)) {
        if (customCancelToken != null) {
            if (customCancelToken != cancelTokens[endopint]) {
                cancelTokens[endopint].cancel(
                    'Operation canceled due to new request.'
                )
            }
        } else {
            cancelTokens[endopint].cancel(
                'Operation canceled due to new request.'
            )
        }
    }

    if (withCancelToken)
        cancelTokens[endopint] = customCancelToken
            ? customCancelToken
            : axios.CancelToken.source()

    const options = {
        method,
        url: `${BASE_URL}${endopint}`,
        data: null,
        payload: null,
        responseType: 'json',
        responseEncoding: 'utf8',
        timeout: timeout ? timeout : 30 * 60 * 1000,

        headers: {
            'Content-Type': contentType,
            Accept: 'application/json'
        },
        cancelToken: withCancelToken ? cancelTokens[endopint].token : null
    }

    if (method === 'GET') {
        options.params = payload
    } else {
        options.data = payload
    }

    if (getAccessToken()) {
        options.headers['Authorization'] = `Bearer ${getAccessToken()}`
    }

    return axiosInstance(options)
        .then((response) => {
            const { accessToken } = response.data.body || {}
            const { statusCode: legacyStatusCode } = response.data || {}

            const isStatusCodeUnauthorized =
                legacyStatusCode === 401 ||
                legacyStatusCode === 403 ||
                response.status === 401 ||
                response.status === 403

            const loginUrl = '/authentication/login.html'
            const registerUrl = '/authentication/register.html'

            if (
                isStatusCodeUnauthorized &&
                accessToken === null &&
                !window?.location?.href?.includes(loginUrl) &&
                !window?.location?.href?.includes(registerUrl)
            ) {
                window.location.href = loginUrl
            }

            // save tokens to storage
            if (
                typeof accessToken != 'undefined' &&
                getAccessToken() !== accessToken
            ) {
                updateAccessToken(accessToken)
            }

            return response
        })
        .catch((error) => {
            if (
                error.response.status === 503 &&
                endopint !== '/api/v2/status'
            ) {
                window.location.href = '/maintenance'
            }

            throw error
        })
}

/**
 * Sends a POST request to a specified endpoint with a payload and optional cancellation token and timeout.
 *
 * @param {string} endopint - The endpoint to which the request will be sent.
 * @param {Object} payload - The data to be sent in the request body.
 * @param {boolean} [withCancelToken=false] - Whether to include a cancel token to allow request cancellation.
 * @param {number|null} [timeout=null] - The request timeout in milliseconds. Defaults to 30 minutes if not provided.
 * @returns {Promise} A promise that resolves when the request completes, triggering a file download.
 */
const sentBlobRequest = (
    endopint,
    payload,
    withCancelToken = false,
    timeout = null
) => {
    if (cancelTokens.hasOwnProperty(endopint)) {
        cancelTokens[endopint].cancel('Operation canceled due to new request.')
    }

    if (withCancelToken) cancelTokens[endopint] = axios.CancelToken.source()

    const options = {
        method: 'POST',
        url: `${BASE_URL}${endopint}`,
        data: payload,
        responseType: 'blob',
        timeout: timeout ? timeout : 30 * 60 * 1000,
        cancelToken: withCancelToken ? cancelTokens[endopint].token : null,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json'
        }
    }

    if (getAccessToken()) {
        options.headers['Authorization'] = `Bearer ${getAccessToken()}`
    }

    return axiosInstance(options).then((response) => {
        let fileName =
            response.headers['content-disposition'].split('filename=')[1]
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            // IE variant
            window.navigator.msSaveOrOpenBlob(
                new Blob([response.data], {
                    type: response.headers['content-type']
                }),
                fileName
            )
        } else {
            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: response.headers['content-type']
                })
            )
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                response.headers['content-disposition'].split('filename=')[1]
            )
            document.body.appendChild(link)
            link.click()
        }
    })
}

// 2. Define token refresh function.
/**
 * Requests a new access token using a refresh token.
 *
 * @param {string} refreshToken - The refresh token used to request a new access token.
 * @returns {Promise<Object>} A promise that resolves to an object containing the new access token and refresh token.
 */
const requestRefresh = (refreshToken) =>
    sentRequest('/auth/refresh_token', { refreshToken }).then((response) => ({
        accessToken: response.data.body.access_token,
        refreshToken: response.data.body.refresh_token
    }))

/**
 * Clears the session by removing authentication tokens.
 *
 * @returns {void} No return value.
 */
const clearSession = () => {
    clearAuthTokens()
}

/**
 * Checks if the user is logged in.
 *
 * @returns {boolean} True if the access token is valid (not empty, null, or undefined), otherwise false.
 */
const isLoggedIn = () =>
    getAccessToken() !== '' &&
    getAccessToken() !== null &&
    getAccessToken() !== undefined
        ? true
        : false

/**
 * Retrieves and returns the current access token.
 *
 * @returns {string} The current access token.
 */
const accessToken = () => getAccessToken()

/**
 * Retrieves and returns the refresh token using the getRefreshToken function.
 *
 * @returns {any} The value returned by the getRefreshToken function.
 */
const refreshToken = () => getRefreshToken()

/**
 * Adds a callback to the list of access token change listeners if it doesn't already exist.
 *
 * @param {string} key - A unique identifier for the callback.
 * @param {function} callback - The function to be called when the access token changes.
 */
const onAccessTokenChanged = (key, callback) => {
    if (
        onAccessTokenChangedCallbacks.filter((item) => item.key == key)
            .length === 0
    )
        onAccessTokenChangedCallbacks.push({ key, callback })
}

/**
 * Generates a response object for errors encountered during operations.
 *
 * @param {Error} err - The error object caught during an operation.
 * @returns {Object} A response object containing details about the error, including status code and error message.
 */
const getCatchResponse = (err) => ({
    body: {},
    statusCode: -1,
    // errorMessage: err.message,
    errorMessage:
        err.message !== 'Operation canceled due to new request.'
            ? err.message
            : null,
    error_stack: null,
    debug: null
})

export default {
    isLoggedIn,
    accessToken,
    refreshToken,
    clearSession,
    requestRefresh,
    sentRequest,
    sentBlobRequest,
    sentFileUploadRequest,
    initialize,
    onAccessTokenChanged,
    getCatchResponse
}
